import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  MapPin,
  CreditCard,
  Hash,
  Tag,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Scissors,
  ShoppingBag,
  ExternalLink,
  ChevronRight,
  Printer,
  Plus,
} from "lucide-react";
import { getBookingDetails } from "../../../services/bookings";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import { useAuth } from "../../../auth/AuthContext";

interface Booking {
  id: number;
  transaction_id: string | null;
  company_id: number;
  coupon_id: number | null;
  user_id: number;
  location_id: number;
  currency_id: number;
  date_time: string;
  status: string;
  booking_type: string;
  payment_gateway: string;
  original_amount: number;
  product_amount: number;
  discount: number;
  discount_percent: number;
  coupon_discount: number | null;
  tax_name: string;
  tax_percent: number;
  tax_amount: number;
  amount_to_pay: number;
  payment_status: string;
  source: string;
  order_id: string | null;
  additional_notes: string | null;
  created_at: string;
  updated_at: string;
  formated_amount_to_pay: string;
  user: any;
  users: any[];
  items: any[];
  booking_payments: any[];
  company: any;
  location: any;
  consultation_forms?: {
    id: number;
    submitted_at: string | null;
    form: {
      title: string;
      [key: string]: any;
    };
    [key: string]: any;
  }[];
}

export default function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await getBookingDetails(parseInt(id));
        if (response && response.booking) {
          setBooking(response.booking);
        } else {
          setError("Booking not found");
        }
      } catch (err) {
        console.error("Failed to load booking details", err);
        setError("Could not load booking details.");
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).toUpperCase();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return `${date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} ${formatTime(dateString)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-700 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "approved":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "canceled":
      case "cancelled":
        return "bg-red-100 text-red-700 border-red-200";
      case "in-progress":
        return "bg-purple-100 text-purple-700 border-purple-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "paid":
        return "bg-green-100 text-green-700";
      case "pending":
      case "unpaid":
        return "bg-yellow-100 text-yellow-700";
      case "partial":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading booking details..." />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h2>
        <p className="text-gray-500 mb-6">{error || "The requested booking could not be found."}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Booking Details"
        icon={Calendar}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Top Summary Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Main Info Card */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

              <div className="relative flex flex-col md:flex-row justify-between items-start gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase borderish ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                    <span className="text-gray-400 text-sm flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {booking.id}
                    </span>
                  </div>

                  {booking.consultation_forms && booking.consultation_forms.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {booking.consultation_forms.map((form) => (
                        <a
                          key={form.id}
                          href={`/consultation-response/${form.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border border-opacity-50 flex items-center gap-1 hover:opacity-80 transition-opacity ${form.submitted_at
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-yellow-100 text-yellow-700 border-yellow-200"
                            }`}
                        >
                          {form.form.title}
                          {form.submitted_at && <CheckCircle size={10} />}
                        </a>
                      ))}
                    </div>
                  )}

                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      {booking.items?.[0]?.business_service?.name || "Service Booking"}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#885ABB]" />
                        <span className="text-sm font-medium">{formatDate(booking.date_time)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#885ABB]" />
                        <span className="text-sm font-medium">{formatTime(booking.date_time)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    {booking.status.toLowerCase() === "completed" ? "Total Paid" : "Total Amount"}
                  </p>
                  <p className="text-4xl font-black text-[#402B69] mt-1">
                    {booking.formated_amount_to_pay}
                  </p>
                  <div className="mt-2">
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${getPaymentStatusColor(booking.payment_status)}`}>
                      Payment: {booking.payment_status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Created At</p>
                  <p className="mt-1 font-semibold text-gray-900">{new Date(booking.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Scissors className="w-5 h-5 text-[#885ABB]" />
                  Services & Products
                </h2>
                <span className="px-3 py-1 bg-purple-50 text-[#885ABB] rounded-full text-xs font-bold">
                  {booking.items?.length || 0} Items
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Staff</th>
                      <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Qty</th>
                      <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Price</th>
                      <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {booking.items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                              {item.product_id ? <ShoppingBag className="w-5 h-5 text-[#885ABB]" /> : <Scissors className="w-5 h-5 text-[#885ABB]" />}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">
                                {item.business_service?.name || item.product?.name || "Custom Item"}
                              </p>
                              <p className="text-xs text-gray-500">{item.business_service?.category?.name || "General"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                              <User className="w-3 h-3 text-gray-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {booking.users?.find(u => u.id === item.employee_id)?.name || "Not assigned"}
                            </span>
                          </div>
                        </td>
                        <td className="px-8 py-5 text-center font-medium text-gray-700">
                          {item.quantity}
                        </td>
                        <td className="px-8 py-5 text-right font-medium text-gray-700">
                          {booking.company?.currency?.currency_symbol || "£"}{Number(item.unit_price).toFixed(2)}
                        </td>
                        <td className="px-8 py-5 text-right font-bold text-gray-900">
                          {booking.company?.currency?.currency_symbol || "£"}{Number(item.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals Section */}
              <div className="bg-gray-50/50 p-8">
                <div className="max-w-xs ml-auto space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 uppercase tracking-wider font-bold text-[10px]">Subtotal</span>
                    <span className="font-semibold text-gray-900">{booking.company?.currency?.currency_symbol || "£"}{Number(booking.original_amount).toFixed(2)}</span>
                  </div>
                  {booking.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-red-500 uppercase tracking-wider font-bold text-[10px]">Discount ({booking.discount_percent}%)</span>
                      <span className="font-semibold text-red-500">-{booking.company?.currency?.currency_symbol || "£"}{Number(booking.discount).toFixed(2)}</span>
                    </div>
                  )}
                  {booking.tax_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 uppercase tracking-wider font-bold text-[10px]">Tax ({booking.tax_percent}%)</span>
                      <span className="font-semibold text-gray-900">{booking.company?.currency?.currency_symbol || "£"}{Number(booking.tax_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm font-black text-gray-900 uppercase">
                      {booking.status.toLowerCase() === "completed" ? "Total Paid" : "Total"}
                    </span>
                    <span className="text-2xl font-black text-[#402B69]">{booking.formated_amount_to_pay}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payments History */}
            {booking.booking_payments && booking.booking_payments.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#885ABB]" />
                    Payment History
                  </h2>
                </div>
                <div className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Method</th>
                          <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Amount</th>
                          <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {booking.booking_payments.map((payment, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-8 py-4 text-sm font-medium text-gray-700">
                              {formatDateTime(payment.paid_on)}
                            </td>
                            <td className="px-8 py-4 text-sm font-medium text-gray-700">
                              {payment.gateway?.replace(/[\\"]/g, '') || "N/A"}
                            </td>
                            <td className="px-8 py-4 text-sm font-bold text-gray-900 text-right">
                              {booking.company?.currency?.currency_symbol || "£"}{Number(payment.amount_paid).toFixed(2)}
                            </td>
                            <td className="px-8 py-4 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>
                                {payment.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-8">
            {/* Customer Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <User size={18} className="text-[#885ABB]" />
                  Customer
                </h3>
                <button
                  onClick={() => navigate(`/summary/customers/${booking.user?.id}`)}
                  className="text-[10px] font-bold text-[#885ABB] uppercase flex items-center gap-1 hover:underline"
                >
                  View Profile <ExternalLink size={12} />
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#885ABB]/10 flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                    {booking.user?.user_image_url ? (
                      <img src={booking.user.user_image_url} alt={booking.user.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-bold text-[#885ABB]">{(booking.user?.name?.[0] || "?").toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">{booking.user?.name || "Unknown"}</h4>
                    <p className="text-sm text-gray-500">{booking.user?.email || "No email provided"}</p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                      <Tag size={14} className="text-[#885ABB]" />
                    </div>
                    <span>{booking.user?.formatted_mobile || "No mobile"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Staff Assigned */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <User size={18} className="text-[#885ABB]" />
                  Team Assigned
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {booking.users?.map((u, idx) => {
                  const assignedServices = booking.items
                    ?.filter((item) => item.employee_id === u.id)
                    ?.map((item) => item.business_service?.name || item.product?.name)
                    ?.filter(Boolean) || [];

                  return (
                    <div
                      key={idx}
                      className="flex flex-col gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full border-2 border-white bg-purple-100 flex items-center justify-center overflow-hidden shadow-sm">
                            {u.user_image_url ? (
                              <img
                                src={u.user_image_url}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User size={20} className="text-[#885ABB]" />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{u.name}</p>
                            <p className="text-[10px] font-bold text-[#885ABB] uppercase tracking-wider">
                              {u.role || u.group || "Practitioner"}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/summary/employees/${u.id}`)}
                          className="p-2 text-gray-400 hover:text-[#885ABB] hover:bg-white rounded-lg transition-all"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>

                      {assignedServices.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                            Assigned Services
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {assignedServices.map((service, sIdx) => (
                              <span
                                key={sIdx}
                                className="px-2 py-1 bg-white border border-purple-100 text-[#885ABB] text-[10px] font-bold rounded-md shadow-sm"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => navigate(`/summary/employees/${u.id}`)}
                        className="w-full py-2.5 bg-white border border-purple-100 text-[#885ABB] text-[10px] font-black uppercase rounded-xl hover:bg-purple-50 transition-all shadow-sm flex items-center justify-center gap-2 mt-1"
                      >
                        View Profile <ExternalLink size={12} />
                      </button>
                    </div>
                  );
                })}
                {(!booking.users || booking.users.length === 0) && (
                  <p className="text-sm text-gray-400 italic text-center py-4">
                    No staff members assigned.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
